// @owners { team: patient-fulfillment }
import { type OriginalShipment } from '@alto/scriptdash/alto/patient_app/scheduling/order_bundling/types/v1/original_shipment';
import { useEffect, useMemo, useState } from 'react';
import { scheduleableSameDayFee, updateOrder } from '~shared/actions/cart';
import { useCartDateInfoKey } from '~shared/features/cart/hooks/useCartDateInfoKey';
import { useCartNextAvailableDate } from '~shared/features/cart/hooks/useCartNextAvailableDate';
import { useCheckoutDateErrors } from '~shared/features/checkout/hooks/useCheckoutDateErrors';
import { getOrderAddress, getOrderDate } from '~shared/features/checkout/selectors/getOrder';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { formatDateStringForAPI } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useQueryShipmentFeesAvailable } from '~shared/queries';
import { exitOrderBundling, initializeOrderBundling } from '~shared/reducers/ui/checkoutFlow';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { getOrderBundlingCheckoutOrigin, useOrderBundlingOptions } from '../../../order-bundling';
import { useCheckoutAddressInfoKeys } from '../../hooks/useCheckoutAddressInfoKeys';
import { type RadioValue } from './types';
import { useBundledEarliestAvailableDate } from './useBundledEarliestAvailableDate';
import {
  computeCheckoutDateItems,
  getIsOrderBundledSelected,
  getIsToday,
  getPreSelectRadioValueForOrder,
} from './utils';

export type CheckoutDateItem = {
  type: RadioValue;
  label: string;
  date: string;
  feeAmountLabel: string;
  feeOriginalAmountLabel: string | undefined;
  description?: string;
  tag: 'expedited' | undefined;
};

type UseSelectorDateCardResult = {
  checkoutDateItems: CheckoutDateItem[];
  radioValue: RadioValue;
  preSelect: RadioValue;
  earliestUpcomingShipment: OriginalShipment | undefined;
  hasSchedulableDeliveryFeesInCart: boolean;
  handleRadioValueChange: (value: RadioValue) => void;
  earliestAvailableDate: string;
  loading: boolean;
};

export const useSelectDateCard = (): UseSelectorDateCardResult => {
  const [radioValue, setRadioValue] = useState<RadioValue>('no-selection');
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const address = useSelectorShared(getOrderAddress);
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const orderDate = useSelectorShared(getOrderDate);
  const scheduleableSameDayFeeRedux = useSelectorShared((state) => state.ui.cart.scheduleableSameDayFee);
  const { isOutOfZone: isOrderAddressOutOfZone } = useCheckoutAddressInfoKeys();
  const { invalidDateKey } = useCartDateInfoKey();
  const { earliestAvailableDate } = useCartNextAvailableDate();
  const {
    availableShipmentFees,
    invalidateShipmentFeesAvailable,
    dateToFeeHash,
    shipmentFeeAvailableHash,
    feeToDateHash,
    hasScheduleableFees,
  } = useQueryShipmentFeesAvailable();
  const orderBundlingOrigin = getOrderBundlingCheckoutOrigin({ isEditingOrder });
  const { earliestUpcomingShipment } = useOrderBundlingOptions({
    origin: orderBundlingOrigin,
    address_id: address?.id,
  });
  // show order bundling
  const orderBundlingDate = earliestUpcomingShipment?.date;
  const isOrderBundlingOptionToday = getIsToday(orderBundlingDate);
  const hasFutureOrderBundlingOption = earliestUpcomingShipment && !isOrderBundlingOptionToday;

  // show earliest available date
  const { showEarliestAvailableDate, earliestAvailableDateIsBeforeOrderBundlingDate } =
    useBundledEarliestAvailableDate();

  // get pre-select radio value and if order bundling is selected
  const hasSchedulableDeliveryFeesInCart = hasScheduleableFees(earliestAvailableDate);
  const preSelect = getPreSelectRadioValueForOrder({
    hasSchedulableDeliveryFeesInCart,
    orderDate,
    dateToFeeHash,
    hasFutureOrderBundlingOption,
    earliestAvailableRadioVisible: showEarliestAvailableDate,
    earliestAvailableDate,
    radioValue,
  });
  const isOrderBundledSelected = getIsOrderBundledSelected(radioValue, preSelect, !!earliestUpcomingShipment);

  // loading
  const fetchNextAvailableDatesLoading = useSelectorShared((state) => state.ui.loading.fetchNextAvailableDatesLoading);
  const { dateValidationErrors } = useCheckoutDateErrors();
  const loading = fetchNextAvailableDatesLoading || dateValidationErrors.some((error) => error.key === 'date_loading');

  const checkoutDateItems = useMemo(() => {
    return computeCheckoutDateItems({
      availableShipmentFees,
      dateToFeeHash,
      earliestAvailableDate,
      earliestAvailableDateIsBeforeOrderBundlingDate,
      feeToDateHash,
      orderBundlingDate,
      orderDate,
      radioValue,
      shipmentFeeAvailableHash,
      showEarliestAvailableDate,
    });
  }, [
    availableShipmentFees,
    dateToFeeHash,
    earliestAvailableDate,
    earliestAvailableDateIsBeforeOrderBundlingDate,
    feeToDateHash,
    orderBundlingDate,
    orderDate,
    radioValue,
    shipmentFeeAvailableHash,
    showEarliestAvailableDate,
  ]);

  useEffect(() => {
    trackEvent({
      event: EVENTS.SELECT_DATE_VIEWED,
      params: {
        delivery_fee_eligible: hasSchedulableDeliveryFeesInCart,
        same_day_fee_amount: shipmentFeeAvailableHash.same_day_delivery?.amount,
        next_day_fee_amount: shipmentFeeAvailableHash.next_day_delivery?.amount,
        standard_fee_amount: shipmentFeeAvailableHash.standard_delivery?.amount,
      },
    });
  }, [
    shipmentFeeAvailableHash.same_day_delivery?.amount,
    shipmentFeeAvailableHash.next_day_delivery?.amount,
    shipmentFeeAvailableHash.standard_delivery?.amount,
    hasSchedulableDeliveryFeesInCart,
    trackEvent,
  ]);

  const hasScheduleableSameDayFee = useMemo(() => {
    return (
      shipmentFeeAvailableHash.same_day_delivery &&
      dateToFeeHash[earliestAvailableDate] === 'same_day_delivery' &&
      !isOrderBundlingOptionToday
    );
  }, [dateToFeeHash, earliestAvailableDate, isOrderBundlingOptionToday, shipmentFeeAvailableHash.same_day_delivery]);

  useEffect(() => {
    if (hasScheduleableSameDayFee) {
      const availableSameDayFee = shipmentFeeAvailableHash.same_day_delivery;
      // only dispatch if fee amount is different
      if (scheduleableSameDayFeeRedux?.amount !== availableSameDayFee?.amount) {
        dispatch(scheduleableSameDayFee(availableSameDayFee));
      }
    } else if (scheduleableSameDayFeeRedux) {
      dispatch(scheduleableSameDayFee(null));
    }
  }, [hasScheduleableSameDayFee, shipmentFeeAvailableHash, scheduleableSameDayFeeRedux, dispatch]);

  // clear available shipment fees cache if checkout date loading
  useEffect(() => {
    if (loading) {
      invalidateShipmentFeesAvailable();
    }
  }, [loading, invalidateShipmentFeesAvailable]);

  // while order bundling preselects and text are based on radio value, we need to set the radio value if any-day-delivery is the only option
  useEffect(() => {
    if (!hasSchedulableDeliveryFeesInCart && !earliestUpcomingShipment) {
      setRadioValue('any-day-delivery');
    }
  }, [setRadioValue, hasSchedulableDeliveryFeesInCart, earliestUpcomingShipment]);

  // clear order date if out of zone or invalid date
  useEffect(() => {
    if (orderDate && (isOrderAddressOutOfZone || invalidDateKey)) {
      dispatch(updateOrder({ date: undefined }));
    }
  }, [orderDate, isOrderAddressOutOfZone, invalidDateKey, dispatch]);

  // automatically select earliest available date if only one option and no option selected
  useEffect(() => {
    if (checkoutDateItems.length === 1 && preSelect === 'no-selection' && earliestAvailableDate) {
      dispatch(updateOrder({ date: formatDateStringForAPI(earliestAvailableDate) }));
    }
  }, [checkoutDateItems.length, preSelect, dispatch, earliestAvailableDate]);

  // reset order bundling
  useEffect(() => {
    if (isOrderBundledSelected) {
      dispatch(initializeOrderBundling());
    } else {
      dispatch(exitOrderBundling());
    }
  }, [isOrderBundledSelected, dispatch]);

  return {
    checkoutDateItems,
    radioValue,
    preSelect,
    earliestUpcomingShipment,
    hasSchedulableDeliveryFeesInCart,
    handleRadioValueChange: setRadioValue,
    earliestAvailableDate,
    loading,
  };
};
